import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, parseISO, isSameDay } from "date-fns";


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats event date and time information, handling both single-day and multi-day events.
 * 
 * For single-day events: returns date and time range (e.g., "Monday, January 1st, 2024" and "9:00 AM - 5:00 PM")
 * For multi-day events: returns date range and time information based on session data
 * 
 * @param startsAt - ISO string of event start date/time
 * @param endsAt - ISO string of event end date/time
 * @param sessions - Optional array of session objects with start/end times and labels
 * @returns Object with date and time strings for display
 */
export function formatEventDateTime(
  startsAt: string | null,
  endsAt: string | null,
  sessions?: Array<{ startsAt: string | null; endsAt: string | null; label?: string | null }>,
) {
  if (!startsAt || !endsAt) {
    return { date: "Date TBD", time: "Time TBD" };
  }
  const startDate = parseISO(startsAt);
  const endDate = parseISO(endsAt);

  const isMultiDay = !isSameDay(startDate, endDate);

  if (isMultiDay) {
    // Multi-day event: show date range
    const dateRange = `${format(startDate, "EEEE, MMMM do")} - ${format(endDate, "EEEE, MMMM do, yyyy")}`;

    if (sessions && sessions.length > 0) {
      // Check if all sessions have the same start/end times
      const firstSession = sessions[0];
      if (!firstSession.startsAt || !firstSession.endsAt) {
        return {
          date: dateRange,
          time: "See details for schedule",
        };
      }
      const firstStartTime = format(parseISO(firstSession.startsAt), "h:mm a");
      const firstEndTime = format(parseISO(firstSession.endsAt), "h:mm a");

      const allSameTimes = sessions.every((session) => {
        if (!session.startsAt || !session.endsAt) {
          return false;
        }
        const sessionStartTime = format(parseISO(session.startsAt), "h:mm a");
        const sessionEndTime = format(parseISO(session.endsAt), "h:mm a");
        return (
          sessionStartTime === firstStartTime && sessionEndTime === firstEndTime
        );
      });

      if (allSameTimes) {
        // All sessions have the same times - show the time range
        return {
          date: dateRange,
          time: `${firstStartTime} - ${firstEndTime}`,
        };
      } else {
        // Different times for different days - show "see details"
        return {
          date: dateRange,
          time: "See details for schedule",
        };
      }
    } else {
      // No session data - just show "Multiple days"
      return {
        date: dateRange,
        time: "Multiple days",
      };
    }
  } else {
    // Single-day event: show date and time range
    return {
      date: format(startDate, "EEEE, MMMM do, yyyy"),
      time: `${format(startDate, "h:mm a")} - ${format(endDate, "h:mm a")}`,
    };
  }
}

/**
 * Formats a date for display (e.g., booking close date)
 * 
 * @param date - Date object or ISO string
 * @returns Formatted date string (e.g., "Friday, June 20th, 2024 at 17:00")
 */
export function formatFriendlyDate(date: Date | string): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, "EEEE, MMMM do, yyyy 'at' HH:mm");
}

/**
 * Formats a date/time for display in lists (e.g. booking submitted date).
 */
export function formatDateTime(date: Date | string): string {
  const d = typeof date === "string" ? parseISO(date) : date;
  return format(d, "d MMM yyyy, HH:mm");
}

/**
 * Formats an event date for display in lists.
 */
export function formatEventDate(date: Date | string | null): string {
  if (!date) return "Date TBD";
  const d = typeof date === "string" ? parseISO(date) : date;
  return format(d, "EEE, d MMM yyyy");
}

/**
 * Checks if booking is still open for an event.
 * 
 * Booking is open if:
 * - Current time is before the booking close date (if specified)
 * - Or current time is before the event start date (if no booking close date)
 * 
 * @param bookingClosesAt - Optional ISO string of when booking closes
 * @param startsAt - ISO string of when the event starts
 * @returns True if booking is still open, false otherwise
 */
export function isBookingOpen(
  bookingClosesAt: string | null | undefined,
  startsAt: string | null,
): boolean {
  if (!startsAt) {
    return false; // Can't determine if booking is open without start date
  }
  const now = new Date();
  const cutoff = bookingClosesAt
    ? new Date(bookingClosesAt)
    : new Date(startsAt);
  return now < cutoff;
}

/**
 * Price point fields used for booking time window + capacity status.
 * `availability` comes from the public API when enriched.
 */
export type BookingPricePointInput = {
  availableFrom?: string | null;
  availableUntil?: string | null;
  availability?: string;
};

/**
 * Booking status: time window (closed / available_soon / open) plus capacity
 * (limited / full) when tiers are in their booking window.
 */
export type BookingStatus =
  | "open"
  | "closed"
  | "available_soon"
  | "limited"
  | "full";

function isPricePointInBookingTimeWindow(
  pp: BookingPricePointInput,
  now: Date,
  bookingEnd: Date,
): boolean {
  const availableFrom = pp.availableFrom
    ? new Date(pp.availableFrom)
    : new Date(0);
  const availableUntil = pp.availableUntil
    ? new Date(pp.availableUntil)
    : bookingEnd;
  return now >= availableFrom && now <= availableUntil;
}

/**
 * Determines booking status for an event (time rules aligned with
 * {@link getAvailablePricePoints}).
 *
 * - "closed": past booking close (or event start if no close date).
 * - "available_soon": before close but no tier is in its time window, or no price points.
 * - "full": every tier in window is at capacity (fully booked).
 * - "limited": at least one bookable tier in window has `nearly_full` availability.
 * - "open": at least one bookable tier in window, none `nearly_full`.
 */
export function getBookingStatus(
  pricePoints: BookingPricePointInput[] | undefined,
  bookingClosesAt: string | null | undefined,
  startsAt: string | null,
): BookingStatus {
  if (!startsAt) {
    return "closed";
  }
  const now = new Date();
  const cutoff = bookingClosesAt
    ? new Date(bookingClosesAt)
    : new Date(startsAt);
  if (now >= cutoff) {
    return "closed";
  }

  const bookingEnd = cutoff;

  if (!pricePoints || pricePoints.length === 0) {
    return "available_soon";
  }

  const inWindow = pricePoints.filter((pp) =>
    isPricePointInBookingTimeWindow(pp, now, bookingEnd),
  );

  if (inWindow.length === 0) {
    return "available_soon";
  }

  if (inWindow.every((pp) => pp.availability === "full")) {
    return "full";
  }

  if (inWindow.some((pp) => pp.availability === "nearly_full" || pp.availability === "full")) {
    return "limited";
  }

  return "open";
}

/**
 * Gets price points that are currently available for booking.
 * 
 * A price point is available if:
 * - Booking is still open (current time < booking end)
 * - Current time is after the price point's availableFrom date (if specified)
 * - Current time is before the price point's availableUntil date (if specified) or booking end
 * 
 * @param pricePoints - Array of price points to filter
 * @param bookingClosesAt - Optional ISO string of when booking closes
 * @param startsAt - ISO string of when the event starts
 * @returns Array of available price points, empty if booking is closed
 */
export function getAvailablePricePoints<
  T extends {
    id: string;
    priceCents: number;
    currency?: string;
    availableFrom?: string | null;
    availableUntil?: string | null;
    availability?: string;
  },
>(
  pricePoints: T[],
  bookingClosesAt: string | null | undefined,
  startsAt: string | null,
): T[] {
  if (!startsAt) {
    return []; // Can't determine availability without start date
  }
  const now = new Date();
  const bookingEnd = bookingClosesAt
    ? new Date(bookingClosesAt)
    : new Date(startsAt);

  // Check if booking is still open
  if (now >= bookingEnd) {
    return []; // Booking closed
  }

  // Filter price points that are currently available
  return pricePoints.filter((pp) => {
    if (pp.availability === "full") {
      return false;
    }

    return isPricePointInBookingTimeWindow(pp, now, bookingEnd);
  });
}

/**
 * Gets all price points that will be available before booking closes, including
 * those that are currently available and those that will become available in the future.
 * 
 * @param pricePoints - Array of price points to filter
 * @param bookingClosesAt - Optional ISO string of when booking closes
 * @param startsAt - ISO string of when the event starts
 * @returns Object with available (now) and future (will be available) price points
 */
export function getAllPricePoints<
  T extends {
    id: string;
    priceCents: number;
    currency?: string;
    availableFrom?: string | null;
    availableUntil?: string | null;
    availability?: string;
  },
>(
  pricePoints: T[],
  bookingClosesAt: string | null | undefined,
  startsAt: string | null,
): {
  available: T[];
  future: T[];
} {
  if (!startsAt) {
    return { available: [], future: [] }; // Can't determine availability without start date
  }
  const now = new Date();
  const bookingEnd = bookingClosesAt
    ? new Date(bookingClosesAt)
    : new Date(startsAt);

  // Check if booking is still open
  if (now >= bookingEnd) {
    return { available: [], future: [] }; // Booking closed
  }

  const available: T[] = [];
  const future: T[] = [];

  pricePoints.forEach((pp) => {
    const availableFrom = pp.availableFrom
      ? new Date(pp.availableFrom)
      : new Date(0);
    const availableUntil = pp.availableUntil
      ? new Date(pp.availableUntil)
      : bookingEnd;

    // Must be available before booking closes
    if (availableFrom >= bookingEnd) {
      return; // This price point won't be available before booking closes
    }

    if (now >= availableFrom && now <= availableUntil) {
      // Currently available (including "full" for display in pricing table)
      available.push(pp);
    } else if (availableFrom > now && availableFrom < bookingEnd) {
      // Will become available in the future (before booking closes)
      future.push(pp);
    }
  });

  return { available, future };
}

/**
 * Formats a price in cents to a display string.
 * 
 * @param cents - Price in cents (e.g., 1000 for £10.00)
 * @param currency - Currency code (e.g., "gbp", "usd"). Defaults to "gbp" if not provided.
 * @returns Formatted price string (e.g., "£10.00" or "10.00 USD")
 */
export function formatPrice(cents: number, currency?: string): string {
  const amount = cents / 100;
  const formattedAmount = amount.toFixed(2);
  const currencyCode = currency || "gbp";
  return currencyCode === "gbp"
    ? `£${formattedAmount}`
    : `${formattedAmount} ${currencyCode.toUpperCase()}`;
}

/**
 * Formats event price, handling price points (single price, price range, or free).
 * 
 * Only includes currently available price points if booking info is provided.
 * Returns:
 * - "Free" if all available prices are 0
 * - Single price if min and max are the same
 * - Price range if min and max differ
 * - "Contact for pricing" if no price points
 * - "Booking Closed", "Fully Booked", or "Available Soon" if no available price points (based on booking status)
 * 
 * @param pricePoints - Optional array of price points
 * @param bookingClosesAt - Optional ISO string of when booking closes
 * @param startsAt - Optional ISO string of when the event starts
 * @returns Formatted price string
 */
export function formatEventPrice(
  pricePoints?: Array<{
    id?: string;
    priceCents: number;
    currency?: string;
    availableFrom?: string | null;
    availableUntil?: string | null;
    availability?: string;
  }>,
  bookingClosesAt?: string | null,
  startsAt?: string | null,
) {
  if (!pricePoints || pricePoints.length === 0) {
    return "Contact for pricing";
  }

  // Filter to only available price points if booking info provided
  // Add id if missing for getAvailablePricePoints
  const pointsWithId = pricePoints.map((pp) => ({
    ...pp,
    id: pp.id || "temp-" + Math.random(),
  }));

  const availablePoints =
    bookingClosesAt !== undefined && startsAt
      ? getAvailablePricePoints(pointsWithId, bookingClosesAt, startsAt)
      : pointsWithId;

  if (availablePoints.length === 0) {
    const status =
      bookingClosesAt !== undefined && startsAt
        ? getBookingStatus(pricePoints, bookingClosesAt, startsAt)
        : "closed";
    if (status === "closed") {
      return "Booking Closed";
    }
    if (status === "full") {
      return "Fully Booked";
    }
    return "Available Soon";
  }

  const prices = availablePoints.map((pp) => pp.priceCents);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const currency = availablePoints[0].currency;

  if (minPrice === 0 && maxPrice === 0) {
    return "Free";
  }

  if (minPrice === maxPrice) {
    return formatPrice(minPrice, currency);
  }

  return `${formatPrice(minPrice, currency)} - ${formatPrice(maxPrice, currency)}`;
}

/**
 * Extracts unique tags from an array of events.
 * Tags are returned as strings (tag names) from the public API.
 * 
 * @param events - Array of events to extract tags from
 * @returns Array of unique tag names
 */
export function extractUniqueTags(events: Array<{ tags?: string[] }>): Array<{ name: string }> {
  const tagSet = new Set<string>();
  
  for (const event of events) {
    if (event.tags && Array.isArray(event.tags)) {
      event.tags.forEach((name) => tagSet.add(name));
    }
  }
  
  return Array.from(tagSet).map((name) => ({ name }));
}
